import type {
  Member,
  NewsArticle,
  Benefit,
  Schedule,
  ScheduleType,
  AssetItem,
  AssetFolder,
  ApiResponse,
  PaginationMeta,
  DiscographyRelease,
} from '@yp/types';

const API_URL = import.meta.env.VITE_API_URL ?? '';

async function authFetch<T>(
  path: string,
  token: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  const data: ApiResponse<T> = await res.json();
  if (!res.ok || !data.success) {
    const err = !data.success ? data.error : { code: 'UNKNOWN', message: 'Request failed' };
    throw new Error(typeof err === 'string' ? err : err.message);
  }
  return (data as { success: true; data: T }).data;
}

// ─── Members ────────────────────────────────────────────────────────────────

export async function getAdminMembers(
  token: string,
): Promise<{ data: Member[]; meta: Partial<PaginationMeta> }> {
  const res = await fetch(`${API_URL}/api/v1/admin/members`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json: ApiResponse<Member[]> = await res.json();
  if (!json.success) throw new Error(typeof json.error === 'string' ? json.error : json.error.message);
  return { data: json.data, meta: json.meta ?? {} };
}

export async function createMember(token: string, body: Partial<Member>): Promise<Member> {
  return authFetch<Member>('/api/v1/admin/members', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateMember(
  token: string,
  id: string,
  body: Partial<Member>,
): Promise<Member> {
  return authFetch<Member>(`/api/v1/admin/members/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deleteMember(token: string, id: string): Promise<void> {
  await authFetch<void>(`/api/v1/admin/members/${id}`, token, { method: 'DELETE' });
}

// ─── News ────────────────────────────────────────────────────────────────────

export async function getAdminNews(
  token: string,
): Promise<{ data: NewsArticle[]; meta: Partial<PaginationMeta> }> {
  const res = await fetch(`${API_URL}/api/v1/admin/news`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json: ApiResponse<NewsArticle[]> = await res.json();
  if (!json.success) throw new Error(typeof json.error === 'string' ? json.error : json.error.message);
  return { data: json.data, meta: json.meta ?? {} };
}

export async function createNews(
  token: string,
  body: Partial<NewsArticle>,
): Promise<NewsArticle> {
  return authFetch<NewsArticle>('/api/v1/admin/news', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateNews(
  token: string,
  id: string,
  body: Partial<NewsArticle>,
): Promise<NewsArticle> {
  return authFetch<NewsArticle>(`/api/v1/admin/news/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deleteNews(token: string, id: string): Promise<void> {
  await authFetch<void>(`/api/v1/admin/news/${id}`, token, { method: 'DELETE' });
}

// ─── Benefits ────────────────────────────────────────────────────────────────

export async function getAdminBenefits(token: string): Promise<Benefit[]> {
  const res = await fetch(`${API_URL}/api/v1/admin/benefits`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json: ApiResponse<Benefit[]> = await res.json();
  if (!json.success) throw new Error(typeof json.error === 'string' ? json.error : json.error.message);
  return json.data;
}

export async function createBenefit(token: string, body: Partial<Benefit>): Promise<Benefit> {
  return authFetch<Benefit>('/api/v1/admin/benefits', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateBenefit(token: string, id: string, body: Partial<Benefit>): Promise<Benefit> {
  return authFetch<Benefit>(`/api/v1/admin/benefits/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deleteBenefit(token: string, id: string): Promise<void> {
  await authFetch<void>(`/api/v1/admin/benefits/${id}`, token, { method: 'DELETE' });
}

// ─── Upload ──────────────────────────────────────────────────────────────────

export interface UploadResult {
  url: string;
  fileId: string;
  name: string;
}

export async function uploadFile(token: string, file: File): Promise<UploadResult> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_URL}/api/v1/admin/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const json: ApiResponse<UploadResult> = await res.json();
  if (!res.ok || !json.success) {
    const err = !json.success ? json.error : { message: 'Upload failed' };
    throw new Error(typeof err === 'string' ? err : err.message);
  }
  return (json as { success: true; data: UploadResult }).data;
}

export async function deleteFile(token: string, fileId: string): Promise<void> {
  await authFetch<void>(`/api/v1/admin/upload/${fileId}`, token, { method: 'DELETE' });
}

// ─── Schedule Types ──────────────────────────────────────────────────────────

export async function getAdminScheduleTypes(token: string): Promise<ScheduleType[]> {
  const res = await fetch(`${API_URL}/api/v1/admin/schedules/types`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json: ApiResponse<ScheduleType[]> = await res.json();
  if (!json.success) throw new Error(typeof json.error === 'string' ? json.error : json.error.message);
  return json.data;
}

// ─── Schedules ───────────────────────────────────────────────────────────────

export async function getAdminSchedules(token: string): Promise<Schedule[]> {
  const res = await fetch(`${API_URL}/api/v1/admin/schedules`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json: ApiResponse<Schedule[]> = await res.json();
  if (!json.success) throw new Error(typeof json.error === 'string' ? json.error : json.error.message);
  return json.data;
}

export async function getAdminScheduleById(token: string, id: string): Promise<Schedule> {
  return authFetch<Schedule>(`/api/v1/admin/schedules/${id}`, token);
}

export async function createSchedule(
  token: string,
  body: Omit<Partial<Schedule>, 'members'> & { members?: { member_id: string; sort_order: number }[] },
): Promise<Schedule> {
  return authFetch<Schedule>('/api/v1/admin/schedules', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateSchedule(
  token: string,
  id: string,
  body: Omit<Partial<Schedule>, 'members'> & { members?: { member_id: string; sort_order: number }[] },
): Promise<Schedule> {
  return authFetch<Schedule>(`/api/v1/admin/schedules/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deleteSchedule(token: string, id: string): Promise<void> {
  await authFetch<void>(`/api/v1/admin/schedules/${id}`, token, { method: 'DELETE' });
}

// ─── Assets ──────────────────────────────────────────────────────────────────

export async function getAssets(token: string, folder?: AssetFolder): Promise<AssetItem[]> {
  const params = folder ? `?folder=${folder}` : '';
  const res = await fetch(`${API_URL}/api/v1/admin/assets${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json: ApiResponse<AssetItem[]> = await res.json();
  if (!json.success) throw new Error(typeof json.error === 'string' ? json.error : json.error.message);
  return json.data;
}

export async function deleteAsset(token: string, id: string): Promise<void> {
  await authFetch<void>(`/api/v1/admin/assets/${id}`, token, { method: 'DELETE' });
}

export async function uploadAsset(
  token: string,
  blob: Blob,
  filename: string,
  feature: AssetFolder,
  width: number,
  height: number
): Promise<AssetItem> {
  const formData = new FormData();
  formData.append('file', blob, filename);
  formData.append('feature', feature);
  formData.append('width', String(width));
  formData.append('height', String(height));
  const res = await fetch(`${API_URL}/api/v1/admin/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const json: ApiResponse<AssetItem> = await res.json();
  if (!res.ok || !json.success) {
    const err = !json.success ? json.error : { message: 'Upload failed' };
    throw new Error(typeof err === 'string' ? err : err.message);
  }
  return (json as { success: true; data: AssetItem }).data;
}

// ─── Discography ─────────────────────────────────────────────────────────────

export async function getAdminDiscography(token: string): Promise<DiscographyRelease[]> {
  const res = await fetch(`${API_URL}/api/v1/admin/discography`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json: ApiResponse<DiscographyRelease[]> = await res.json();
  if (!json.success) throw new Error(json.error.message);
  return json.data;
}

export async function getAdminReleaseById(token: string, id: string): Promise<DiscographyRelease> {
  return authFetch<DiscographyRelease>(`/api/v1/admin/discography/${id}`, token);
}

export interface ReleasePayload {
  slug: string;
  title: string;
  type: string;
  cover_url?: string;
  release_date?: string;
  date_precision?: string;
  streaming_links?: Record<string, string>;
  description?: string;
  is_published?: boolean;
  order_index?: number;
  members?: { member_id: string; role_in_release?: string; sort_order?: number }[];
}

export async function createRelease(token: string, body: ReleasePayload): Promise<DiscographyRelease> {
  return authFetch<DiscographyRelease>('/api/v1/admin/discography', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateRelease(token: string, id: string, body: Partial<ReleasePayload>): Promise<DiscographyRelease> {
  return authFetch<DiscographyRelease>(`/api/v1/admin/discography/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deleteRelease(token: string, id: string): Promise<void> {
  await authFetch<void>(`/api/v1/admin/discography/${id}`, token, { method: 'DELETE' });
}
